import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";
import { reportUrl } from "../lib/vectorsurv";

/**
 * Status panel for the dead bird report watcher (ROADMAP B12).
 *
 * The same shape as the VectorSurv Sync Bot's and the verification watcher's:
 * one layout for one question, "is the job nobody can see actually working?".
 * Activity strip, live log with the heartbeat, detail, recipients.
 *
 * Three signals, kept separate because they fail separately:
 *   - heartbeat: a 30s timer on the server. Proves the host and systemd are alive.
 *   - timer armed: systemd's own view of pioneer-carcass-watch.timer. A heartbeat
 *     cannot see that this one timer was stopped, which would leave new dead
 *     birds unannounced while everything else looked healthy.
 *   - runs: the watcher itself. Proves VectorSurv, the boundary layer and the
 *     mailer all work, but is silent for half an hour at a time.
 *
 * Report ids and verdicts only. Nothing about a caller exists on this page.
 */

const UPTIME_DAYS = 90;

type DayBucket = { runs: number; failed: number; changed: number };

type HistoryEntry = {
    at?: string;
    ok?: boolean;
    checked?: number;
    new?: number;
    notified?: number;
    outside?: number;
    note?: string;
    error?: string;
};

type Heartbeat = { at?: string; boot?: string; age_seconds?: number; alive?: boolean; error?: string };

interface WatchStatus {
    installed: boolean;
    health: "healthy" | "stale" | "failing" | "unknown";
    ok?: boolean;
    message?: string;
    last_run_local?: string | null;
    error?: string | null;
    checked?: number | null;
    new?: number | null;
    notified?: number | null;
    outside?: number | null;
    window_days?: number;
    counties?: string[];
    schedule?: string;
    heartbeat?: Heartbeat | null;
    timer?: { armed?: boolean; state?: string; next_run_local?: string | null } | null;
    history?: HistoryEntry[];
    daily?: Record<string, DayBucket>;
    announced?: { id: string; date?: string; verdict?: string; at?: string }[];
    problems?: { title: string; detail: string }[];
    recipients?: { to?: string[]; cc?: string[] };
    notify?: { to?: string[]; cc?: string[] };
}

const HEALTH: Record<string, { label: string; dot: string; text: string; blurb: string }> = {
    healthy: {
        label: "Active", dot: "bg-green-500", text: "text-green-700",
        blurb: "Running on schedule. New reports inside the District are emailed as they arrive.",
    },
    stale: {
        label: "Stale", dot: "bg-amber-500", text: "text-amber-700",
        blurb: "A run was due and hasn't happened. New dead bird reports are not being announced.",
    },
    failing: {
        label: "Failing", dot: "bg-red-500", text: "text-red-700",
        blurb: "The last run failed. Nothing is lost: the reports it missed are announced when it recovers.",
    },
    unknown: {
        label: "Not installed", dot: "bg-slate-400", text: "text-slate-600",
        blurb: "The watcher hasn't run on this host.",
    },
};

type LogLine = { key: string; at: string; kind: "beat" | "run" | "warn"; text: string };

function clockTime(iso: string) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "--:--:--" : d.toLocaleTimeString([], { hour12: false });
}

function dayKeys(days: number) {
    const out: string[] = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    }
    return out;
}

function describeRun(h: HistoryEntry) {
    if (!h.ok) return `run failed — ${h.error || "no reason recorded"}`;
    if (h.note === "baseline") return `baseline: ${h.checked ?? 0} existing report(s) marked as seen, nothing sent`;
    const n = h.notified ?? 0;
    if (n > 0) return `${n} new report${n === 1 ? "" : "s"} emailed${h.outside ? `, ${h.outside} outside the District` : ""}`;
    return `checked ${h.checked ?? 0} · nothing new${h.outside ? ` (${h.outside} outside the District)` : ""}`;
}

export default function CarcassWatchStatus() {
    const [data, setData] = useState<WatchStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [lines, setLines] = useState<LogLine[]>([]);
    const logRef = useRef<HTMLDivElement | null>(null);
    const autoScroll = useRef(true);
    const seenRuns = useRef<Set<string>>(new Set());

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get(`/carcass-watch-status?_=${Date.now()}`);
            const d: WatchStatus = r.data;
            setData(d);
            setError(null);

            // The log is built from what the poll returns: a heartbeat line each
            // tick, and a run line the first time each run is seen.
            const next: LogLine[] = [];
            if (d.heartbeat?.at) {
                next.push({
                    key: `beat-${d.heartbeat.at}`, at: d.heartbeat.at, kind: "beat",
                    text: `heartbeat ok (age ${Math.round(d.heartbeat.age_seconds ?? 0)}s)`,
                });
            }
            for (const h of d.history ?? []) {
                if (!h.at || seenRuns.current.has(h.at)) continue;
                seenRuns.current.add(h.at);
                next.push({ key: `run-${h.at}`, at: h.at, kind: h.ok ? "run" : "warn", text: describeRun(h) });
            }
            if (next.length) {
                setLines((prev) => [...prev, ...next.filter((l) => !prev.some((p) => p.key === l.key))].slice(-200));
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not read the watcher's status.");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
        // Every 30s, to match the heartbeat it displays.
        const t = window.setInterval(load, 30000);
        return () => window.clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (autoScroll.current && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [lines]);

    const h = HEALTH[data?.health || "unknown"] || HEALTH.unknown;
    const daily = data?.daily || {};
    const series = dayKeys(UPTIME_DAYS).map((date) => ({ date, bucket: daily[date] }));
    const totalRuns = Object.values(daily).reduce((n, b) => n + b.runs, 0);
    const totalFailed = Object.values(daily).reduce((n, b) => n + b.failed, 0);
    const activeDays = Object.values(daily).filter((b) => b.runs > 0).length;
    const uptimePct = totalRuns === 0 ? null : ((totalRuns - totalFailed) / totalRuns) * 100;
    const beatAlive = !!data?.heartbeat?.alive;

    const barColour = (b?: DayBucket) => {
        if (!b || b.runs === 0) return "bg-slate-200";
        if (b.failed === 0) return "bg-green-500";
        if (b.failed >= b.runs) return "bg-red-500";
        return "bg-amber-400";
    };
    const lineColour = (k: LogLine["kind"]) => (k === "warn" ? "text-red-400" : k === "run" ? "text-sky-300" : "text-slate-400");

    return (
        <div className="space-y-5 text-sm" data-testid="carcass-watch-status">
            <p className="text-ink-soft text-[13px]">
                Every half hour it asks VectorSurv for the last {data?.window_days ?? 14} days of dead bird reports,
                places any it has not seen against the District boundary, and emails the ones inside it, or that
                could not be placed, to the people below. Each report is announced once.
            </p>

            <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-baseline justify-between mb-2">
                    <span className="text-xs text-slate-500">Watcher runs over the past {UPTIME_DAYS} days</span>
                    <span className={`text-xs font-semibold ${h.text}`}>{h.label}</span>
                </div>
                <div className="flex items-end gap-[2px] h-10">
                    {series.map((d) => (
                        <div
                            key={d.date}
                            title={
                                d.bucket
                                    ? `${d.date}: ${d.bucket.runs} runs, ${d.bucket.failed} failed, ${d.bucket.changed} report(s) announced`
                                    : `${d.date}: no runs (before install)`
                            }
                            className={`flex-1 min-w-[3px] h-full rounded-sm ${barColour(d.bucket)}`}
                        />
                    ))}
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400">
                    <span>{UPTIME_DAYS} days ago</span>
                    <span className="text-slate-600">
                        {uptimePct === null ? "no data yet" : `${uptimePct.toFixed(1)} % of ${totalRuns} runs succeeded`}
                        <span className="text-slate-400"> · {activeDays} active day{activeDays === 1 ? "" : "s"}</span>
                    </span>
                    <span>Today</span>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">Live activity</span>
                    <span className="flex items-center gap-1.5 text-xs">
                        <span className={`inline-block h-2 w-2 rounded-full ${beatAlive ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
                        <span className={beatAlive ? "text-green-700" : "text-red-700"}>
                            {beatAlive ? "heartbeat live" : "no heartbeat"}
                        </span>
                    </span>
                </div>
                <div
                    ref={logRef}
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        autoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
                    }}
                    className="h-40 overflow-y-auto rounded-lg bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed"
                >
                    {lines.length === 0 ? (
                        <div className="text-slate-500">waiting for the next heartbeat…</div>
                    ) : (
                        lines.map((l) => (
                            <div key={l.key} className={lineColour(l.kind)}>
                                <span className="text-slate-600">{clockTime(l.at)}</span>{" "}
                                {l.kind === "beat" ? "·" : l.kind === "warn" ? "!" : "▸"} {l.text}
                            </div>
                        ))
                    )}
                </div>
                <div className="mt-1 text-[11px] text-slate-400">
                    Heartbeat every 30s proves the server and its timers are alive. The watcher itself runs every half
                    hour — that is the line that proves VectorSurv, the boundary layer and the mailer all work.
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${h.dot}`} />
                <span className={`font-semibold ${h.text}`}>{h.label}</span>
                <span className="text-slate-500">— {h.blurb}</span>
                <button onClick={load} disabled={busy} className="btn-ghost text-xs !py-1 ml-auto">
                    {busy ? "Reading…" : "Refresh"}
                </button>
            </div>

            {error && <p className="text-[13px] text-wnv">{error}</p>}

            {data?.timer && data.timer.armed === false && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
                    <div className="font-medium">The timer is not armed</div>
                    <div className="text-xs">
                        systemd reports pioneer-carcass-watch.timer as {data.timer.state || "inactive"}. Nothing will run
                        until it is started, however healthy everything else looks.
                    </div>
                </div>
            )}

            {data?.installed === false && <p className="text-[12px] text-ink-faint">{data.message}</p>}

            {data?.installed && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <Fact label="Last run" value={data.last_run_local || "—"} />
                    <Fact label="Next run" value={data.timer?.next_run_local || "—"} />
                    <Fact label="Schedule" value={data.schedule || "—"} />
                    <Fact label="Watching" value={`${(data.counties || []).join(", ") || "—"} County, last ${data.window_days ?? 14} days`} />
                    <Fact
                        label="Server up since"
                        value={data.heartbeat?.boot ? data.heartbeat.boot.replace("T", " ").slice(0, 19) : "—"}
                    />
                </div>
            )}

            {!!data?.problems?.length && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
                    <div className="font-medium">Needs attention</div>
                    {data.problems.map((p, i) => (
                        <div key={i} className="mt-1">
                            <div className="font-medium text-[13px]">{p.title}</div>
                            <div className="text-xs">{p.detail}</div>
                        </div>
                    ))}
                </div>
            )}

            {data?.installed && data.checked != null && (
                <div>
                    <div className="text-xs text-slate-500 mb-1">What the last run did</div>
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead className="bg-paper-2">
                                <tr>
                                    {["Reports in the window", "Not seen before", "Emailed", "Outside the District"].map((x) => (
                                        <th key={x} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{x}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-t border-slate-200">
                                    <td className="px-3 py-2">{data.checked ?? "—"}</td>
                                    <td className="px-3 py-2">{data.new ?? "—"}</td>
                                    <td className="px-3 py-2">{data.notified ?? "—"}</td>
                                    <td className="px-3 py-2 text-slate-500">{data.outside ?? "—"}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!!data?.announced?.length && (
                <div>
                    <div className="text-xs text-slate-500 mb-1">Recently emailed</div>
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-[13px]">
                            <tbody>
                                {data.announced.map((a) => (
                                    <tr key={a.id} className="border-t border-slate-200 first:border-t-0">
                                        <td className="px-3 py-1.5 font-mono text-[12px]">
                                            {reportUrl(a.id) ? (
                                                <a
                                                    href={reportUrl(a.id)}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="text-brand-deep underline underline-offset-2"
                                                    title="Open this report in VectorSurv, where it can be printed"
                                                >
                                                    {a.id}
                                                </a>
                                            ) : (
                                                a.id
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-slate-500">{a.date || "—"}</td>
                                        <td className="px-3 py-1.5 text-slate-500">
                                            {a.verdict === "needs a look" ? "needed a look" : a.verdict}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
                {data?.notify?.to?.length ? (
                    <div className="text-xs">
                        <div className="text-slate-500">New reports are emailed to</div>
                        <div className="font-medium">{data.notify.to.join(", ")}</div>
                        {data.notify.cc?.length ? <div className="text-slate-500">cc {data.notify.cc.join(", ")}</div> : null}
                    </div>
                ) : null}
                {data?.recipients?.to?.length ? (
                    <div className="text-xs">
                        <div className="text-slate-500">Alerts about the watcher itself go to</div>
                        <div className="font-medium">{data.recipients.to.join(", ")}</div>
                        {data.recipients.cc?.length ? <div className="text-slate-500">cc {data.recipients.cc.join(", ")}</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function Fact({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-slate-500 text-xs">{label}</div>
            <div className="font-medium">{value}</div>
        </div>
    );
}
